import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Repository } from '../../types';
import {
  repositoryService,
  OAuthConnection,
  OrganizationsResponse,
} from '../../services/RepositoryService';
import api from '../../services/authenticatedApi';
import { logger } from '../../utils/logger';
import { useJWTAuth } from '../../contexts/useJWTAuth';
import { hasRequiredScopes } from './helpers';
import { useConnectionSelection } from './useConnectionSelection';
import { useOrganizationsData } from './useOrganizationsData';
import { useRepoFilters } from './useRepoFilters';
import { ProviderTab, ReauthConnection } from './types';

export interface UseRepositoryDataResult {
  user: ReturnType<typeof useJWTAuth>['user'];
  isAuthenticated: boolean;
  authLoading: boolean;
  searchParams: URLSearchParams;
  activeTab: ProviderTab;
  searchQuery: string;
  repositories: Repository[];
  connections: OAuthConnection[];
  providerConnections: OAuthConnection[];
  activeConnection: OAuthConnection | undefined;
  loading: boolean;
  loadingRepos: boolean;
  syncing: boolean;
  error: string | null;
  errorStack: string | null;
  totalRepos: number;
  reauthConnections: ReauthConnection[];
  orgsData: OrganizationsResponse | null;
  connectingProvider: ProviderTab | null;
  selectedOrganization: string;
  selectedVisibility: string;
  selectedLanguage: string;
  selectedScanStatus: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  currentPage: number;
  itemsPerPage: number;
  needsConnection: boolean;
  tokenInitialized: boolean;
  setSelectedOrganization: (v: string) => void;
  setSelectedVisibility: (v: string) => void;
  setSelectedLanguage: (v: string) => void;
  setSelectedScanStatus: (v: string) => void;
  setSortBy: (v: string) => void;
  setSortOrder: (v: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')) => void;
  setCurrentPage: (v: number | ((prev: number) => number)) => void;
  setError: (v: string | null) => void;
  setErrorStack: (v: string | null) => void;
  setReauthConnections: (v: ReauthConnection[]) => void;
  setConnectingProvider: (v: ProviderTab | null) => void;
  updateUrlParams: (updates: { provider?: ProviderTab; q?: string }) => void;
  handleConnectionSelect: (connectionId: string) => void;
  loadConnections: () => Promise<void>;
  loadRepositories: () => Promise<void>;
  handleSync: () => Promise<void>;
}

export function useRepositoryData(): UseRepositoryDataResult {
  const { user, isAuthenticated, isLoading: authLoading } = useJWTAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [connections, setConnections] = useState<OAuthConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const searchQuery = searchParams.get('q') ?? '';
  const [error, setError] = useState<string | null>(null);
  const [errorStack, setErrorStack] = useState<string | null>(null);
  const [totalRepos, setTotalRepos] = useState(0);
  const [tokenInitialized, setTokenInitialized] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reauthConnections, setReauthConnections] = useState<ReauthConnection[]>([]);
  const [connectingProvider, setConnectingProvider] = useState<ProviderTab | null>(null);

  const loadingReposRef = useRef(false);
  const hasSearchedRef = useRef(false);
  const hasAutoSynced = useRef(false);

  const {
    selectedOrganization,
    selectedVisibility,
    selectedLanguage,
    selectedScanStatus,
    sortBy,
    sortOrder,
    currentPage,
    itemsPerPage,
    setSelectedOrganization,
    setSelectedVisibility,
    setSelectedLanguage,
    setSelectedScanStatus,
    setSortBy,
    setSortOrder,
    setCurrentPage,
    resetFilters,
  } = useRepoFilters();

  const activeTab = (searchParams.get('provider') as ProviderTab) || 'github';
  const connectionStatus = searchParams.get('status');

  const { selectedConnectionIds, updateConnectionSelection } = useConnectionSelection(connections);

  const providerConnections = useMemo(
    () => connections.filter((conn) => conn.provider === activeTab),
    [connections, activeTab],
  );

  const activeConnection = useMemo(() => {
    if (providerConnections.length === 0) return undefined;
    const selectedId = selectedConnectionIds[activeTab];
    if (selectedId) {
      const selected = providerConnections.find((conn) => conn.id === selectedId);
      if (selected) return selected;
    }
    return providerConnections[0];
  }, [providerConnections, selectedConnectionIds, activeTab]);

  const updateUrlParams = (updates: { provider?: ProviderTab; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (updates.provider !== undefined) {
      params.set('provider', updates.provider);
    }
    if (updates.q !== undefined) {
      if (updates.q === '') {
        params.delete('q');
      } else {
        params.set('q', updates.q);
      }
    }
    setSearchParams(params, { replace: true });
  };

  const loadConnections = async () => {
    try {
      const isConnected = await repositoryService.checkConnection();
      if (!isConnected) {
        throw new Error(
          'Unable to connect to the server. Please ensure the backend service is running and try again.',
        );
      }
      const conns = await repositoryService.getConnections();
      setConnections(conns);
    } catch (err) {
      logger.error('[OAuth] loadConnections: Failed', { error: err });
      const errorMessage = err instanceof Error ? err.message : 'Failed to load connections';
      setError(errorMessage);
      setErrorStack(err instanceof Error ? err.stack || null : null);
    }
  };

  const loadRepositories = async () => {
    if (loadingReposRef.current) return;
    loadingReposRef.current = true;
    setLoadingRepos(true);
    setError(null);

    try {
      const isConnected = await repositoryService.checkConnection();
      if (!isConnected) {
        throw new Error(
          'Unable to connect to the server. Please ensure the backend service is running and try again.',
        );
      }

      if (activeConnection) {
        const repoResponse = await repositoryService.listRepositories({
          provider: activeTab,
          page: 1,
          limit: 1000,
          search: searchQuery || undefined,
        });
        const filteredRepos = repoResponse.repositories.filter((repo) => {
          if (!activeConnection?.id) return true;
          return !repo.connectionId || repo.connectionId === activeConnection.id;
        });
        setRepositories(filteredRepos);
        setTotalRepos(filteredRepos.length);
      } else if (repositories.length === 0) {
        setRepositories([]);
        setTotalRepos(0);
      }
    } catch (err) {
      logger.error('[OAuth] loadRepositories: Failed', { error: err });
      const errorMessage = err instanceof Error ? err.message : 'Failed to load repositories';
      setError(errorMessage);
      setErrorStack(err instanceof Error ? err.stack || null : null);
    } finally {
      loadingReposRef.current = false;
      setLoadingRepos(false);
    }
  };

  const loadReauthStatus = async () => {
    try {
      const response = await api.get('/oauth/reauth-status');
      const { needsReauth, connections: reauthConns } = response.data;
      if (needsReauth) {
        const filtered = reauthConns.filter(
          (conn: ReauthConnection) => conn.provider === activeTab,
        );
        setReauthConnections(filtered);
      }
    } catch (err: unknown) {
      logger.error('Failed to load reauth status', { error: err });
    }
  };

  const handleSync = async () => {
    if (syncing || !activeConnection) return;

    setSyncing(true);
    setError(null);

    try {
      const response = await api.post(`/oauth/${activeTab}/sync`);
      logger.info(`[Sync] Synced ${response.data.synced} repositories from ${activeTab}`);
      await loadRepositories();
    } catch (err) {
      logger.error(`[Sync] Failed to sync ${activeTab}`, { error: err });
      const errorMessage =
        err instanceof Error ? err.message : `Failed to sync ${activeTab} repositories`;
      setError(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const handleConnectionSelect = (connectionId: string) => {
    updateConnectionSelection(activeTab, connectionId);
    setCurrentPage(1);
  };

  useEffect(() => {
    const initialize = async () => {
      if (authLoading) return;
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      try {
        setTokenInitialized(true);
        await loadConnections();
        await loadReauthStatus();
        setLoading(false);
      } catch (err) {
        logger.error('[OAuth] Failed to initialize', { error: err });
        setError(err instanceof Error ? err.message : 'Failed to initialize');
        setLoading(false);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user]);

  useEffect(() => {
    if (authLoading || !user || !tokenInitialized) return;

    if (!activeConnection) {
      setRepositories([]);
      setTotalRepos(0);
      setLoadingRepos(false);
      return;
    }

    const timer = setTimeout(() => {
      loadRepositories();
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, tokenInitialized, activeConnection, activeTab]);

  useEffect(() => {
    if (authLoading || !user || !tokenInitialized || !activeConnection) return;

    if (searchQuery) {
      hasSearchedRef.current = true;
    }

    if (!searchQuery && repositories.length === 0 && !hasSearchedRef.current) return;

    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadRepositories();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (
      connectionStatus === 'connected' &&
      activeConnection &&
      !syncing &&
      !loadingRepos &&
      repositories.length === 0 &&
      !hasAutoSynced.current &&
      tokenInitialized
    ) {
      hasAutoSynced.current = true;

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      setSearchParams(newParams, { replace: true });

      handleSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    connectionStatus,
    activeConnection,
    syncing,
    loadingRepos,
    repositories.length,
    tokenInitialized,
    activeTab,
  ]);

  useEffect(() => {
    if (tokenInitialized) {
      loadReauthStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tokenInitialized]);

  const orgsData = useOrganizationsData({
    tokenInitialized,
    activeConnection,
    activeTab,
    reloadRepositories: loadRepositories,
  });

  useEffect(() => {
    resetFilters();
    // resetFilters is a stable setter wrapper; depending on it would re-run on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
    // setCurrentPage is a stable useState setter and never needs to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOrganization,
    selectedVisibility,
    selectedLanguage,
    selectedScanStatus,
    sortBy,
    sortOrder,
  ]);

  const needsConnection =
    !activeConnection ||
    (!hasRequiredScopes(activeConnection) && totalRepos === 0 && !loadingRepos);

  return {
    user,
    isAuthenticated,
    authLoading,
    searchParams,
    activeTab,
    searchQuery,
    repositories,
    connections,
    providerConnections,
    activeConnection,
    loading,
    loadingRepos,
    syncing,
    error,
    errorStack,
    totalRepos,
    reauthConnections,
    orgsData,
    connectingProvider,
    selectedOrganization,
    selectedVisibility,
    selectedLanguage,
    selectedScanStatus,
    sortBy,
    sortOrder,
    currentPage,
    itemsPerPage,
    needsConnection,
    tokenInitialized,
    setSelectedOrganization,
    setSelectedVisibility,
    setSelectedLanguage,
    setSelectedScanStatus,
    setSortBy,
    setSortOrder,
    setCurrentPage,
    setError,
    setErrorStack,
    setReauthConnections,
    setConnectingProvider,
    updateUrlParams,
    handleConnectionSelect,
    loadConnections,
    loadRepositories,
    handleSync,
  };
}
